import React, { useEffect, useMemo, useState } from 'react';
import { useChatContext } from '../../contexts/useChatContext';
import { useCrmData } from '../../contexts/CrmProviders';
import { ChatLayout, ChatDetails } from './components/layout';
import ChatWindow from './components/ChatWindow';
import ChatSidebar from './components/ChatSidebar';
import { ErrorMessage } from './components/ErrorMessage';
import { useChatAutoStart } from './hooks/useChatAutoStart';
import { useChatMarkAsRead } from './hooks/useChatMarkAsRead';
import { useChatHandlers } from './hooks/useChatHandlers';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { useAutoSyncAvatar } from './hooks/useAutoSyncAvatar';
import { conversationDisplayName } from './hooks/contactPanelDeals.helpers';
import { resolveConversationConnectionScope } from './lib/conversationScope';

/** Desktop 2xl: painel em coluna; abaixo disso overlay / inicia fechado (evita clip). */
const CONTACT_PANEL_COLUMN_MQ = '(min-width: 1536px)';

const Chat: React.FC = () => {
    const {
        conversations,
        activeConversationId,
        activeConnectionId,
        setActiveConversationId,
        activeConversation,
        startNewConversation,
        sendMessage,
        editMessage,
        deleteMessage,
        sendMedia,
        sendContactCard,
        sendLocationMessage,
        sendPixMessage,
        assignConversation,
        transferConversation,
        closeConversation,
        reopenConversation,
        updateConversation,
        currentUserId,
        isLoading,
        isLoadingConnections,
        isLoadingMessages,
        error,
        clearError,
        retryLastMessage,
        isRetrying,
        loadOlderMessages,
        isLoadingOlder,
        hasMoreMessages,
        isLoadingMoreConversations,
        hasMoreConversations,
        loadMoreConversations,
        backendCounters,
        selectedConnectionId,
        selectedConnection,
        whatsappLive,
        setInboxChannelConnectionId,
    } = useChatContext();

    const { leads, contacts } = useCrmData();
    const { isOnline } = useNetworkStatus();

    const conversationsWithSavedName = useMemo(
        () => conversations.map((conversation) => {
            const name = conversationDisplayName(conversation, contacts);
            return name === conversation.name ? conversation : { ...conversation, name };
        }),
        [conversations, contacts],
    );
    const activeWithSavedName = useMemo(() => {
        if (!activeConversation) return activeConversation;
        const name = conversationDisplayName(activeConversation, contacts);
        return name === activeConversation.name ? activeConversation : { ...activeConversation, name };
    }, [activeConversation, contacts]);

    const isChatReady = Boolean(selectedConnectionId) && !isLoadingConnections;
    useChatAutoStart(startNewConversation, isChatReady);
    useChatMarkAsRead(
        activeConversationId,
        activeConversation?.connectionId,
        updateConversation,
    );
    useAutoSyncAvatar(
        conversations,
        !isLoading && whatsappLive === 'online',
        selectedConnection?.instanceName,
        selectedConnectionId,
    );

    const { handleNewConversation, handleTransfer } = useChatHandlers({
        startNewConversation,
        transferConversation,
        activeConversationId,
        leads,
    });

    const handleCloseConversation = async () => {
        if (!activeConversationId) return;
        const success = await closeConversation(activeConversationId);
        if (success) setActiveConversationId(null);
    };

    const handleReopenConversation = async () => {
        if (!activeConversationId) return;
        await reopenConversation(activeConversationId);
    };

    const handleAssignToMe = activeConversationId
        ? () => assignConversation(activeConversationId)
        : undefined;

    const [isContactPanelOpen, setIsContactPanelOpen] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(CONTACT_PANEL_COLUMN_MQ).matches,
    );
    const toggleContactPanel = () => setIsContactPanelOpen((prev) => !prev);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!window.matchMedia(CONTACT_PANEL_COLUMN_MQ).matches) {
            setIsContactPanelOpen(false);
        }
    }, [activeConversationId]);

    // Ao encolher abaixo de 2xl, fecha a coluna — overlay só sob demanda do usuário.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia(CONTACT_PANEL_COLUMN_MQ);
        const onChange = () => {
            if (!mq.matches) setIsContactPanelOpen(false);
        };
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    return (
        <ChatLayout
            hasActiveSelection={!!activeConversationId}
            error={error && (
                <ErrorMessage
                    message={error}
                    onRetry={retryLastMessage}
                    onDismiss={clearError}
                    isOnline={isOnline}
                    isRetrying={isRetrying}
                />
            )}
            sidebar={
                <ChatSidebar
                    conversations={conversationsWithSavedName}
                    activeConversationId={activeConversationId}
                    activeConnectionId={activeConnectionId}
                    onSelectConversation={setActiveConversationId}
                    onNewConversation={handleNewConversation}
                    currentUserId={currentUserId}
                    isLoadingMore={isLoadingMoreConversations}
                    hasMoreConversations={hasMoreConversations}
                    onLoadMore={loadMoreConversations}
                    backendCounters={backendCounters}
                    instanceName={selectedConnection?.instanceName}
                    connectionId={selectedConnectionId}
                    onChannelConnectionChange={setInboxChannelConnectionId}
                />
            }
            main={
                <ChatWindow
                    conversation={activeWithSavedName}
                    onSendMessage={sendMessage}
                    onEditMessage={editMessage}
                    onDeleteMessage={deleteMessage}
                    onSendMedia={sendMedia}
                    onSendContact={sendContactCard}
                    onSendLocation={sendLocationMessage}
                    onSendPix={sendPixMessage}
                    onAssignToMe={handleAssignToMe}
                    onTransfer={handleTransfer}
                    onCloseConversation={handleCloseConversation}
                    onReopenConversation={handleReopenConversation}
                    currentUserId={currentUserId}
                    isLoadingMessages={isLoadingMessages}
                    selectedTagIds={activeWithSavedName?.tags || []}
                    onBack={() => setActiveConversationId(null)}
                    isContactPanelOpen={isContactPanelOpen}
                    onToggleContactPanel={toggleContactPanel}
                    onLoadOlderMessages={loadOlderMessages}
                    isLoadingOlder={isLoadingOlder}
                    hasMoreMessages={hasMoreMessages}
                />
            }
            details={activeWithSavedName && isContactPanelOpen && (
                <ChatDetails
                    conversation={activeWithSavedName}
                    connectionId={resolveConversationConnectionScope(activeWithSavedName.id, {
                        conversationConnectionId: activeWithSavedName.connectionId,
                        selectedConnectionId,
                    }) ?? undefined}
                    onClose={() => setIsContactPanelOpen(false)}
                />
            )}
        />
    );
};

export default Chat;
